import { useState, useEffect } from 'react';
import { getRandomNumber } from '../../../../../Utils/NumberUtils';
import { Grading } from '@mui/icons-material';

export const useRunningDistance = ({
  isRunning = false,
  speedKmh = 0, // Option 1: use speed in km/h
  initialDistance = 0, // Starting distance in km
}: {
  isRunning?: boolean;
  speedKmh?: number;
  paceMinPerKm?: number;
  initialDistance?: number;
}) => {
  const [distance, setDistance] = useState<number>(initialDistance);

  useEffect(() => {
    if (!isRunning) return;

    if (speedKmh <= 0) return;

    // Distance increment per second (in km)
    const distancePerSecond = speedKmh / 3600; // km/h → km/s

    const interval = setInterval(() => {
      setDistance((prev) => prev + distancePerSecond);
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [isRunning, speedKmh]);

  // Optional: reset distance when stopped
  const reset = () => setDistance(initialDistance);

  return { distance: parseFloat(distance.toFixed(3)), reset };
};

// Realistic heart rate zones based on speed (km/h)
// These are approximate averages for a fit adult
const getBaseHeartRateForSpeed = (speedKmh: number): number => {
  if (speedKmh <= 0) return 70; // Resting
  if (speedKmh <= 3) return 90; // Very light (walking/jog)
  if (speedKmh <= 6) return 110; // Very light (walking/jog)
  if (speedKmh <= 9) return 135; // Light
  if (speedKmh <= 12) return 155; // Moderate
  if (speedKmh <= 15) return 170; // Hard
  return 180 + (speedKmh - 15) * 2; // Very hard (caps around 190–200)
};

export const useHeartRate = ({
  speedKmh = 0,
  isRunning = false,
  restingHR = 70,
  maxHR = 190,
  updateInterval = 2000, // Update every 2 seconds for natural feel
}: {
  speedKmh?: number;
  isRunning?: boolean;
  restingHR?: number;
  maxHR?: number;
  updateInterval?: number;
}) => {
  const [heartRate, setHeartRate] = useState<number>(restingHR);

  useEffect(() => {
    if (!isRunning) {
      setHeartRate(0);
      return;
    }

    // When running: fluctuate around target based on speed
    const interval = setInterval(() => {
      const baseHR = getBaseHeartRateForSpeed(speedKmh);
      const targetHR = Math.min(baseHR, maxHR);

      // Add natural randomness: ±5–12 bpm fluctuation
      const randomness = Math.random() * 12 - 6; // -6 to +6
      const breathingVariation = Math.sin(Date.now() / 1000) * 4; // Gentle wave

      let newHR = Math.round(targetHR + randomness + breathingVariation);

      // Clamp to realistic bounds
      newHR = Math.max(restingHR + 10, Math.min(newHR, maxHR));

      // Smooth transition toward target
      setHeartRate((prev) => {
        const diff = newHR - prev;
        return Math.abs(diff) < 1 ? newHR : Math.round(prev + diff * 0.3);
      });
    }, updateInterval);

    return () => clearInterval(interval);
  }, [isRunning, speedKmh, restingHR, maxHR, updateInterval]);

  const reset = () => setHeartRate(0);

  return { heartRate: Math.round(heartRate), reset };
};

export const useCalories = ({
  isRunning,
  speedKmh,
}: {
  isRunning: boolean;
  speedKmh: number;
}) => {
  const [calories, setCalories] = useState(0);

  useEffect(() => {
    if (!isRunning) return;

    // Distance increment per second (in km)
    const distancePerSecond = speedKmh / 3600; // km/h → km/s

    console.log('distancePerSecond::: ', distancePerSecond);

    const interval = setInterval(() => {
      setCalories(
        (prev) => prev + distancePerSecond * getRandomNumber(80, 100)
      );
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [isRunning, speedKmh, setCalories]);

  const reset = () => setCalories(0);

  return { calories, reset };
};

export const useMoves = ({
  isRunning,
  speedKmh,
}: {
  isRunning: boolean;
  speedKmh: number;
}) => {
  const [moves, setMoves] = useState(0);

  useEffect(() => {
    if (!isRunning) return;

    const distancePerSecond = speedKmh / 3600; // km/h → km/s

    const interval = setInterval(() => {
      setMoves((prev) => prev + distancePerSecond * getRandomNumber(20, 40));
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [isRunning, speedKmh, setMoves]);

  const reset = () => setMoves(0);

  return { moves, reset };
};

export const useElevation = ({
  isRunning,
  speedKmh,
  gradient,
}: {
  isRunning: boolean;
  speedKmh: number;
  gradient: number;
}) => {
  const [elevation, setElevation] = useState(0);

  useEffect(() => {
    if (!isRunning || gradient <= 0) return;

    const distancePerSecond = speedKmh / 3600; // km/h → km/s

    const interval = setInterval(() => {
      setElevation(
        (prev) => prev + distancePerSecond * getRandomNumber(60, 80) * gradient
      );
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [isRunning, speedKmh, setElevation, gradient]);

  const reset = () => setElevation(0);

  return { elevation, reset };
};
